// Prefer a runtime override first:
// - URL param: ?apiBase=https://your-backend (persists to localStorage)
// - localStorage: localStorage.setItem('API_BASE_OVERRIDE', 'https://your-backend')
// - window.__RUNTIME_CONFIG__ = { API_BASE: 'https://your-backend' }
// Falls back to build-time env, then localhost for development.

let urlParamOverride: string | undefined = undefined;
if (typeof window !== 'undefined') {
  try {
    const params = new URLSearchParams(window.location.search);
    const p = params.get('apiBase') || params.get('api_base');
    if (p && /^https?:\/\//i.test(p)) {
      urlParamOverride = p.replace(/\/+$/, '');
      try { localStorage.setItem('API_BASE_OVERRIDE', urlParamOverride); } catch {}
    }
  } catch {}
}

const runtimeOverride =
  urlParamOverride ||
  (typeof window !== 'undefined' &&
    ((window as any).__RUNTIME_CONFIG__?.API_BASE || localStorage.getItem('API_BASE_OVERRIDE'))) ||
  undefined;

const RAW_API_BASE = runtimeOverride || process.env.REACT_APP_API_BASE || 'http://localhost:8002';
export const API_BASE = RAW_API_BASE.replace(/\/+$/, '');

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  status: number;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    if (response.ok) {
      try {
        const data = await response.json();
        return { data, status: response.status };
      } catch {
        return { status: response.status };
      }
    } else {
      try {
        const errorData = await response.json();
        return { 
          error: errorData.detail || `HTTP ${response.status}`, 
          status: response.status 
        };
      } catch {
        return { 
          error: `HTTP ${response.status}`, 
          status: response.status 
        };
      }
    }
  }

  async get<T>(endpoint: string, opts?: { signal?: AbortSignal }): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
        signal: opts?.signal,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return { error: 'Network error', status: 0 };
    }
  }

  async post<T>(endpoint: string, data: any, opts?: { signal?: AbortSignal }): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
        signal: opts?.signal,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return { error: 'Network error', status: 0 };
    }
  }

  async put<T>(endpoint: string, data: any, opts?: { signal?: AbortSignal }): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
        signal: opts?.signal,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return { error: 'Network error', status: 0 };
    }
  }

  async delete(endpoint: string, opts?: { signal?: AbortSignal }): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        signal: opts?.signal,
      });
      return this.handleResponse<void>(response);
    } catch (error) {
      return { error: 'Network error', status: 0 };
    }
  }

  async upload<T>(endpoint: string, formData: FormData, opts?: { signal?: AbortSignal }): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData,
        signal: opts?.signal,
      });
      return this.handleResponse<T>(response);
    } catch (error) {
      return { error: 'Network error', status: 0 };
    }
  }
}

export const apiClient = new ApiClient();
export default apiClient;

